const mn = '[cynqueModules/navDrawerOverflowXResizeObserver.ts]'

const RETRY_INTERVAL_MS = 200
const MAX_ATTACH_ATTEMPTS = 25 // ~5s, then give up rather than poll forever

/**
 * Attach `observer` to the nav drawer's content node, retrying until it exists.
 *
 * ⚠ The retry has to re-pass `observer` explicitly. `setTimeout(thisFn, ms)` calls
 * it with NO arguments — the timer's own arg is the delay, not a payload — so the
 * destructured `{ observer }` parameter read off `undefined` and threw
 * "Cannot destructure property 'observer' of 'undefined'" from inside the timer,
 * where nothing catches it. The observer was then never attached for the rest of
 * the session: the nav simply stopped resizing its type, with the only evidence an
 * uncaught TypeError in the console. That is a silent, whole-feature failure
 * whenever the node isn't up yet at the layout's `mounted()`, which is a race.
 *
 * Retries are bounded for the same reason the layout's teardown is guarded: setup
 * runs on every mount including ones with no drawer (logged out), and an unbounded
 * 200ms poll in that state never stops.
 *
 * Returns the observed element, or `undefined` if the node wasn't there yet — the
 * caller can't be handed an element that doesn't exist. A later retry that DOES
 * attach can't backfill that return, so the caller's element stays `undefined` and
 * `removeNavDrawerResizeObserver` falls back to `disconnect()`. That is correct:
 * disconnect is strictly stronger than unobserve, and it also cancels the pending
 * frame (see `createNavDrawerResizeObserver`).
 */
export function addResizeObserverWhenDesiredNodeAvailable({
  observer,
  attempt = 1,
}: {
  observer: ResizeObserver
  attempt?: number
}): Element | undefined {
  const fn = `${mn} addResizeObserverWhenDesiredNodeAvailable |`
  const elArray = document.getElementsByClassName(
    'v-navigation-drawer__content'
  )
  const el = elArray[0]

  if (!el) {
    if (attempt >= MAX_ATTACH_ATTEMPTS) {
      console.debug(
        `${fn} node still absent after ${attempt} attempts. Giving up.`
      )
      return
    }
    console.debug(
      `${fn} node doesn't exist yet. Waiting ${RETRY_INTERVAL_MS}ms to try again` +
        ` (attempt ${attempt} of ${MAX_ATTACH_ATTEMPTS}).`
    )
    window.setTimeout(() => {
      addResizeObserverWhenDesiredNodeAvailable({
        observer,
        attempt: attempt + 1,
      })
    }, RETRY_INTERVAL_MS)
    return
  }
  console.debug(`${fn} node exists. Starting resizeObserver.`)
  observer.observe(el)
  return el
}

/**
 * Decide the shrink state from a measurement, WITH HYSTERESIS.
 *
 * Pure so it can be tested without a DOM; see the co-located spec.
 *
 * The asymmetry is the entire point. Entering the shrunk state needs only
 * overflow, but LEAVING it cannot be decided the same way, because the shrink is
 * itself what removed the overflow. Test symmetrically (`scrollHeight >
 * clientHeight` both ways) and the two states each recreate the other's trigger:
 *
 *   overflowing -> shrink type -> no longer overflowing -> restore type ->
 *   overflowing -> ...
 *
 * which is the resize loop this file used to drive. See the header comment on
 * `createNavDrawerResizeObserver` for why that loop only closed off-macOS.
 *
 * ⚠ The exit test CANNOT be a headroom band on `scrollHeight`. Per spec,
 * `scrollHeight` is clamped to at least `clientHeight` — once the content fits it
 * reports the container's own height, not the content's. So `scrollHeight` is
 * *never* less than `clientHeight`, any `scrollHeight <= clientHeight - band`
 * test is dead code, and the drawer latches shrunk forever. (Measuring a band
 * this way passes a unit test only if the test feeds it a `scrollHeight` below
 * `clientHeight`, which no real DOM produces.)
 *
 * So compare against the one number that IS still meaningful: while overflowing,
 * `scrollHeight` is unclamped and reports the true full-size content height.
 * `createNavDrawerResizeObserver` captures it at the moment it shrinks, and this
 * restores the full-size type once the container has genuinely grown enough to
 * hold it. That is exact rather than a guard band, and it cannot oscillate — after
 * restoring, the full-size content fits by construction.
 */
export function decideIsOverflowXed({
  scrollHeight,
  clientHeight,
  isCurrentlyOverflowXed,
  fullSizeContentHeight,
}: {
  scrollHeight: number
  clientHeight: number
  isCurrentlyOverflowXed: boolean
  fullSizeContentHeight?: number
}): boolean {
  if (!isCurrentlyOverflowXed) {
    return scrollHeight > clientHeight
  }
  // Shrunk with no recorded full-size height (we started shrunk, or the content
  // changed under us): stay shrunk rather than guess. The next genuine shrink
  // transition records one.
  if (fullSizeContentHeight === undefined) {
    return true
  }
  return clientHeight < fullSizeContentHeight
}

/**
 * Observe the nav drawer's content and shrink its type when it overflows.
 *
 * The nav shrinks its type when its content overflows vertically (a scrollbar is
 * needed) — both to keep more of it on screen and to compensate for the scrollbar.
 * Overflow is detected DIRECTLY (scrollHeight vs clientHeight) rather than by
 * watching the content WIDTH as the original did: macOS overlay scrollbars don't
 * reduce the content width, so the width comparison never saw the scrollbar and
 * latched the shrunk state on permanently (270px < the 271px baseline on the very
 * first fire).
 *
 * Both state questions are about the scrollbar, which is what the shrink exists to
 * compensate for. They just can't both be asked of the CURRENT DOM:
 *
 *   enter shrunk  — is a scrollbar needed now?          scrollHeight > clientHeight
 *   leave shrunk  — would one still be needed at FULL   clientHeight >= the
 *                   size type?                          full-size content height
 *
 * The second can't be measured while shrunk: the DOM in front of us is the shrunk
 * one, and `scrollHeight` clamps to `clientHeight` the moment the content fits, so
 * it stops reporting how tall the content actually wants to be. `fullSizeContentHeight`
 * below captures that number at the one instant it is observable — the shrink
 * transition, when the content is still full size AND still overflowing, so
 * `scrollHeight` is unclamped and exact.
 *
 * ⚠ Measuring height is what makes this observer's own effect one of its inputs,
 * so the two protections below are load-bearing, not defensive dressing:
 *
 * 1. `decideIsOverflowXed` is hysteretic, so the states can't trigger each other.
 * 2. The state write is deferred to the NEXT frame. A ResizeObserver callback that
 *    mutates layout during delivery leaves notifications pending when delivery
 *    ends, and the browser reports that to `window.onerror` as "ResizeObserver
 *    loop completed with undelivered notifications" — which Honeybadger then files
 *    as a stackless, unattributable client error.
 *
 * Neither reproduces on macOS. The loop closes through the observed element's
 * CONTENT-BOX WIDTH, which only changes when a classic scrollbar takes layout
 * space; macOS overlay scrollbars don't, so the callback's effect never feeds back
 * and the observer looks well-behaved on the development machine while looping on
 * every Windows and Linux client.
 */
export function createNavDrawerResizeObserver({
  callbackIsOverflowXed,
  callbackIsNoLongerOverflowXed,
  getIsCurrentlyOverflowXed,
}: {
  callbackIsOverflowXed: Function
  callbackIsNoLongerOverflowXed: Function
  getIsCurrentlyOverflowXed: () => boolean
}): ResizeObserver {
  // Coalesces bursts of observations into one deferred decision, and guarantees
  // at most one pending frame regardless of how often the observer fires.
  let pendingFrame: number | undefined

  // The content's height at FULL-SIZE type, captured on the shrink transition —
  // the only moment it is observable (see the header comment). Undefined until
  // then, and re-captured on every subsequent shrink so nav content that changes
  // (items added, a group expanded) doesn't leave a stale threshold behind.
  let fullSizeContentHeight: number | undefined

  const cancelPendingFrame = () => {
    if (pendingFrame !== undefined) {
      window.cancelAnimationFrame(pendingFrame)
      pendingFrame = undefined
    }
  }

  const observer = new ResizeObserver((entries) => {
    const fn = `${mn} navDrawerResizeObserver |`
    const entry = entries.at(-1)
    if (!entry) {
      return
    }
    // Read (cheap, already-computed layout) inside the callback; WRITE outside it.
    const el = entry.target as HTMLElement
    const { scrollHeight, clientHeight } = el

    if (pendingFrame !== undefined) {
      window.cancelAnimationFrame(pendingFrame)
    }
    pendingFrame = window.requestAnimationFrame(() => {
      pendingFrame = undefined
      const isCurrentlyOverflowXed = getIsCurrentlyOverflowXed()
      const isOverflowXed = decideIsOverflowXed({
        scrollHeight,
        clientHeight,
        isCurrentlyOverflowXed,
        fullSizeContentHeight,
      })
      if (isOverflowXed === isCurrentlyOverflowXed) {
        return
      }
      console.debug(
        `${fn} scrollHeight ${scrollHeight} vs clientHeight ${clientHeight}` +
          ` (full-size content ${fullSizeContentHeight ?? 'unmeasured'})` +
          ` -> overflowing: ${isOverflowXed}`
      )
      if (isOverflowXed) {
        // Capture BEFORE the callback shrinks the type — this is the last frame
        // in which scrollHeight reports the full-size content height.
        fullSizeContentHeight = scrollHeight
        callbackIsOverflowXed()
      } else {
        fullSizeContentHeight = undefined
        callbackIsNoLongerOverflowXed()
      }
    })
  })

  // Deferring the write opens a window the old synchronous version didn't have:
  // the layout can unmount between an observation and the frame that acts on it,
  // leaving a queued callback that writes to a component that is going away.
  // Fold the cancel into teardown rather than widening
  // `removeNavDrawerResizeObserver`'s signature, so disconnecting stays the one
  // thing a caller has to remember. (See `295f0add` for the last teardown bug
  // this file grew.)
  const nativeDisconnect = observer.disconnect.bind(observer)
  const nativeUnobserve = observer.unobserve.bind(observer)
  observer.disconnect = () => {
    cancelPendingFrame()
    nativeDisconnect()
  }
  observer.unobserve = (target: Element) => {
    cancelPendingFrame()
    nativeUnobserve(target)
  }

  return observer
}

/**
 * Tear the observer down. Tolerates never having been set up.
 *
 * `resizeObserver` is genuinely optional, so it is typed that way: the layout's
 * `mounted()` hook returns early when there is no logged-in user, which leaves
 * `navDrawerOverflowXResizeObserver` undefined, while `beforeUnmount()` tears
 * down unconditionally. Without the guard that path throws "Cannot read
 * properties of undefined (reading 'disconnect')".
 *
 * It only started throwing on Vue 3: under Vue 2 the hook was spelled
 * `beforeDestroy`, and during the migration that name was silently never called
 * (see the note on the hook in layouts/default.vue), so the mismatch existed but
 * never ran. Setup is conditional, so teardown has to be too.
 */
export function removeNavDrawerResizeObserver({
  resizeObserver,
  el,
}: {
  resizeObserver: ResizeObserver | undefined
  el: Element | undefined
}) {
  const fn = `${mn} removeNavDrawerResizeObserver |`
  console.debug(`${fn} removing navDrawerOverflowXResizeObserver`)
  if (!resizeObserver) {
    console.debug(`${fn} no observer was created — nothing to remove`)
    return
  }
  if (el) {
    console.debug(`${fn} unobserving`)
    resizeObserver.unobserve(el)
  } else {
    console.debug(`${fn} disconnecting`)
    resizeObserver.disconnect()
  }
}
